import React, { useState } from 'react';
import { Navigation } from '../components/Navigation';
import { Section } from '../components/ui/Section';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import { motion, AnimatePresence } from 'framer-motion';
import { Bike, DollarSign, Clock, MapPin, TrendingUp, Shield, Users, CheckCircle, ArrowRight, X, Upload, Calendar, Phone, Mail, Award } from 'lucide-react';
const benefits = [{
  icon: DollarSign,
  title: 'Earn ₦50k - ₦150k/month',
  desc: 'Get paid per verification + bonuses'
}, {
  icon: Clock,
  title: 'Flexible Hours',
  desc: 'Work on your own schedule'
}, {
  icon: Bike,
  title: 'Free Bicycle',
  desc: 'Branded bike provided'
}, {
  icon: Shield,
  title: 'Insurance Coverage',
  desc: 'Full accident & health coverage'
}, {
  icon: TrendingUp,
  title: 'Career Growth',
  desc: 'Leadership opportunities'
}, {
  icon: Users,
  title: 'Community',
  desc: 'Join 120+ ambassadors'
}];
const requirements = ['Must be a current student or recent graduate', 'Own a smartphone with camera', 'Available 15-20 hours per week', 'Strong communication skills', 'Reliable and detail-oriented', 'Passion for helping fellow students'];
const testimonials = [{
  name: 'Chioma A.',
  role: 'Simba Ambassador',
  university: 'UNILAG',
  image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  quote: 'Being a Simba Ambassador has been amazing! I earn extra income while helping students find safe housing. The flexible hours work perfectly with my class schedule.',
  earnings: '₦120k/month',
  verifications: 45
}, {
  name: 'David O.',
  role: 'Lead Ambassador',
  university: 'Yaba Tech',
  image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  quote: 'Started as an ambassador 6 months ago, now I lead a team of 10. The growth opportunities are real, and the impact we make is incredible.',
  earnings: '₦180k/month',
  verifications: 120
}, {
  name: 'Sarah K.',
  role: 'Simba Ambassador',
  university: 'Covenant',
  image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  quote: 'Love the community and the mission. Every property I verify helps keep students safe. Plus, the pay is great for a part-time gig!',
  earnings: '₦95k/month',
  verifications: 38
}];
export function AmbassadorsPage() {
  const [showApplication, setShowApplication] = useState(false);
  const [step, setStep] = useState<'info' | 'verification' | 'success'>('info');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    university: '',
    studentId: '',
    availability: '',
    experience: '',
    motivation: ''
  });
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setStep('verification');
    setTimeout(() => {
      setStep('success');
    }, 2000);
  };
  return <div className="min-h-screen bg-slate-50 pt-20" data-id="element-395" data-mp="36qns58g72gnw">
      <Navigation data-id="element-396" data-mp="7a7m9plq3zt5" />

      {/* Application Modal */}
      <AnimatePresence data-id="element-397" data-mp="7bshzijrxosl">
        {showApplication && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto" onClick={() => setShowApplication(false)} data-id="element-398" data-mp="37ul7ehx8q2p">
            <motion.div initial={{
          scale: 0.9,
          y: 20
        }} animate={{
          scale: 1,
          y: 0
        }} exit={{
          scale: 0.9,
          y: 20
        }} className="bg-white rounded-2xl max-w-2xl w-full my-8" onClick={e => e.stopPropagation()} data-id="element-399" data-mp="m5xcjzxxyz3o">
              <AnimatePresence mode="wait" data-id="element-400" data-mp="1q4999nbld2j">
                {step === 'info' && <motion.div key="info" initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} data-id="element-401" data-mp="pfymhsbjx3nb">
                    <div className="flex justify-between items-center p-6 border-b border-slate-200" data-id="element-402" data-mp="18cgapzr6ylet">
                      <h3 className="text-2xl font-bold text-brand-dark" data-id="element-403" data-mp="1d6ubyisjbmre">
                        Join the Simba Fleet
                      </h3>
                      <button onClick={() => setShowApplication(false)} className="p-2 hover:bg-slate-100 rounded-full transition-colors" data-id="element-404" data-mp="1ih6hcixjidpd">
                        <X className="w-5 h-5" data-id="element-405" data-mp="1rbuipxzzvb8w" />
                      </button>
                    </div>

                    <form onSubmit={handleSubmit} className="p-6 space-y-4" data-id="element-406" data-mp="13lh1z3mv0ytm">
                      <div className="grid grid-cols-2 gap-4" data-id="element-407" data-mp="zkf0sh9ducp0">
                        <Input label="First Name" value={formData.firstName} onChange={e => setFormData({
                    ...formData,
                    firstName: e.target.value
                  })} required data-id="element-408" data-mp="1dj4z207xuiey" />
                        <Input label="Last Name" value={formData.lastName} onChange={e => setFormData({
                    ...formData,
                    lastName: e.target.value
                  })} required data-id="element-409" data-mp="2ryv2bew6ny00" />
                      </div>

                      <Input label="Email Address" type="email" value={formData.email} onChange={e => setFormData({
                  ...formData,
                  email: e.target.value
                })} required data-id="element-410" data-mp="2db08twacpplv" />

                      <Input label="Phone Number" type="tel" value={formData.phone} onChange={e => setFormData({
                  ...formData,
                  phone: e.target.value
                })} placeholder="+234 800 000 0000" required data-id="element-411" data-mp="1ujh9bzfjctol" />

                      <div className="grid grid-cols-2 gap-4" data-id="element-412" data-mp="14370t205pp3i">
                        <Input label="University" value={formData.university} onChange={e => setFormData({
                    ...formData,
                    university: e.target.value
                  })} placeholder="e.g. UNILAG" required data-id="element-413" data-mp="zbp1eids7wkn" />
                        <Input label="Student ID" value={formData.studentId} onChange={e => setFormData({
                    ...formData,
                    studentId: e.target.value
                  })} required data-id="element-414" data-mp="gexc9sbk6n8o" />
                      </div>

                      <div data-id="element-415" data-mp="1lh6my9adaoby">
                        <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-416" data-mp="1lgwveh9oc945">
                          Weekly Availability
                        </label>
                        <select value={formData.availability} onChange={e => setFormData({
                    ...formData,
                    availability: e.target.value
                  })} className="w-full h-10 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary" required data-id="element-417" data-mp="1bm4xtdg3dm9d">
                          <option value="" data-id="element-418" data-mp="wyeeoiqrde9n">Select hours per week</option>
                          <option value="10-15" data-id="element-419" data-mp="ryrxjy7t7dvk">10-15 hours</option>
                          <option value="15-20" data-id="element-420" data-mp="rxguk0m6ua5i">15-20 hours</option>
                          <option value="20+" data-id="element-421" data-mp="16v0v8vc1xj5v">20+ hours</option>
                        </select>
                      </div>

                      <div data-id="element-422" data-mp="11wg55oqikfv5">
                        <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-423" data-mp="11v50znt5p63i">
                          Relevant Experience (Optional)
                        </label>
                        <textarea value={formData.experience} onChange={e => setFormData({
                    ...formData,
                    experience: e.target.value
                  })} placeholder="Any previous experience in customer service, sales, or similar roles..." className="w-full h-24 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary resize-none" data-id="element-424" data-mp="1bnkiyl4gduoe" />
                      </div>

                      <div data-id="element-425" data-mp="3vcnek2wu9oru">
                        <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-426" data-mp="3vbubpd103jez">
                          Why do you want to join? *
                        </label>
                        <textarea value={formData.motivation} onChange={e => setFormData({
                    ...formData,
                    motivation: e.target.value
                  })} placeholder="Tell us what motivates you to become a Simba Ambassador..." className="w-full h-24 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary resize-none" required data-id="element-427" data-mp="3mbp5353msp1v" />
                      </div>

                      <div className="pt-4" data-id="element-428" data-mp="2alj0w9drjakg">
                        <Button type="submit" className="w-full" size="lg" data-id="element-429" data-mp="2eltjcliqrtg7">
                          Submit Application
                          <ArrowRight className="ml-2 w-5 h-5" data-id="element-430" data-mp="2jjv1934flmq3" />
                        </Button>
                      </div>
                    </form>
                  </motion.div>}

                {step === 'verification' && <motion.div key="verification" initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} className="p-12 text-center" data-id="element-431" data-mp="2tgrcvhpmmoyv">
                    <div className="w-20 h-20 bg-brand-primary/20 rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse" data-id="element-432" data-mp="m6laxgww8nzm">
                      <Upload className="w-10 h-10 text-brand-primary" data-id="element-433" data-mp="w37u819kn6zj" />
                    </div>
                    <h3 className="text-2xl font-bold text-brand-dark mb-2" data-id="element-434" data-mp="1a2zdohiuxfkx">
                      Processing Application
                    </h3>
                    <p className="text-slate-600" data-id="element-435" data-mp="1534p4kmk1231">
                      Please wait while we review your information...
                    </p>
                  </motion.div>}

                {step === 'success' && <motion.div key="success" initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} className="p-12 text-center" data-id="element-436" data-mp="1ew1xvz1qvcog">
                    <motion.div initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} transition={{
                delay: 0.2,
                type: 'spring'
              }} className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-437" data-mp="2cequ1ozx5hkr">
                      <CheckCircle className="w-10 h-10 text-emerald-600" data-id="element-438" data-mp="1fqkrr8qgz19n" />
                    </motion.div>
                    <h3 className="text-2xl font-bold text-brand-dark mb-2" data-id="element-439" data-mp="1jquxqle9hroi">
                      Application Submitted!
                    </h3>
                    <p className="text-slate-600 mb-6" data-id="element-440" data-mp="1aqg9pe9wxyhb">
                      Thank you for applying! We'll review your application and
                      get back to you within 48 hours.
                    </p>
                    <div className="bg-slate-50 rounded-xl p-4 mb-6" data-id="element-441" data-mp="291hi5hirmez3">
                      <p className="text-sm text-slate-700" data-id="element-442" data-mp="24b117nfqkb7z">
                        <strong data-id="element-443" data-mp="2496i8eo0yt6j">Next Steps:</strong> Check your email for a
                        confirmation and interview scheduling link.
                      </p>
                    </div>
                    <Button onClick={() => setShowApplication(false)} data-id="element-444" data-mp="1ueeip5avtw94">
                      Close
                    </Button>
                  </motion.div>}
              </AnimatePresence>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Hero Section */}
      <Section className="!pt-16 !pb-12 bg-gradient-to-br from-brand-dark via-brand-dark-secondary to-brand-dark text-white relative overflow-hidden" data-id="element-445" data-mp="1gewtwpxl6izr">
        <div className="absolute inset-0 opacity-10" style={{
        backgroundImage: 'radial-gradient(circle, #b583c9 1px, transparent 1px)',
        backgroundSize: '40px 40px'
      }} data-id="element-446" data-mp="3aqtmim7rluwe"></div>

        <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-447" data-mp="3n7m879terwar">
          {/* Left: Text Content */}
          <div data-id="element-448" data-mp="turcthlcox7">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="inline-flex items-center gap-2 px-4 py-2 bg-brand-primary/20 rounded-full text-brand-primary text-sm font-medium mb-6" data-id="element-449" data-mp="udyrg9m7uwz">
              <Bike className="w-4 h-4" data-id="element-450" data-mp="3dbj8eam149qz" />
              <span data-id="element-451" data-mp="3dckj2dz4uu86">Join 120+ Active Ambassadors</span>
            </motion.div>

            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className="text-5xl md:text-6xl font-bold mb-6" data-id="element-452" data-mp="ok8blxtxpjh8">
              Become a{' '}
              <span className="text-brand-primary" data-id="element-453" data-mp="2ymcqwiwqgamd">Simba Ambassador</span>
            </motion.h1>

            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="text-xl text-slate-300 mb-8 max-w-2xl" data-id="element-454" data-mp="2tnrp4f1u1ivt">
              Earn money, gain experience, and make a real impact by verifying
              student housing on your campus.
            </motion.p>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} className="flex flex-col sm:flex-row gap-4" data-id="element-455" data-mp="1n6xtxf4ixir9">
              <Button size="lg" className="bg-brand-primary hover:bg-brand-light text-white" onClick={() => setShowApplication(true)} data-id="element-456" data-mp="2ibwks1rtoy4">
                Apply Now
                <ArrowRight className="ml-2 w-5 h-5" data-id="element-457" data-mp="hebcupz1avec" />
              </Button>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10" data-id="element-458" data-mp="hc6zlhf8wk1e">
                Learn More
              </Button>
            </motion.div>

            <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.5
          }} className="mt-12 grid grid-cols-3 gap-8" data-id="element-459" data-mp="ceoyuebdtb4h">
              <div data-id="element-460" data-mp="3otacxzx3svec">
                <div className="text-3xl font-bold text-brand-primary mb-1" data-id="element-461" data-mp="3ots4wkcqgngg">
                  ₦50k+
                </div>
                <div className="text-sm text-slate-400" data-id="element-462" data-mp="3jsmfaveddgb5">
                  Avg. Monthly Earnings
                </div>
              </div>
              <div data-id="element-463" data-mp="39vzsw26eah70">
                <div className="text-3xl font-bold text-brand-primary mb-1" data-id="element-464" data-mp="39wj83b4nurhc">
                  120+
                </div>
                <div className="text-sm text-slate-400" data-id="element-465" data-mp="bdkstmxzlui9">Active Ambassadors</div>
              </div>
              <div data-id="element-466" data-mp="6fjaj48dxz7u">
                <div className="text-3xl font-bold text-brand-primary mb-1" data-id="element-467" data-mp="6h434ob53832">
                  850+
                </div>
                <div className="text-sm text-slate-400" data-id="element-468" data-mp="3sultzh1ww8ax">
                  Properties Verified
                </div>
              </div>
            </motion.div>
          </div>

          {/* Right: Ambassador Image */}
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.4
        }} className="relative" data-id="element-469" data-mp="pdde32qqviiu">
            <div className="relative" data-id="element-470" data-mp="16my4nk7y3mwq">
              <img src="https://cdn.magicpatterns.com/uploads/6UJzVB6EDgU4sSkAXBJJLn/Copy_of_Billvast_Shirt_Mock-up_(1).png" alt="Simba Ambassador on Bicycle" className="w-full h-auto max-w-lg mx-auto drop-shadow-2xl" data-id="element-471" data-mp="16lwfycy0b1wj" />
              <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl p-4 shadow-xl" data-id="element-472" data-mp="1klo0yxmxxik1">
                <div className="text-sm text-slate-500 mb-1" data-id="element-473" data-mp="1pf0eabm662xh">
                  This could be you!
                </div>
                <div className="text-2xl font-bold text-brand-primary" data-id="element-474" data-mp="1uc0iegzgkvly">
                  ₦120k/mo
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </Section>

      {/* Benefits Section */}
      <Section data-id="element-475" data-mp="32tu4a3do3ajp">
        <div className="text-center mb-12" data-id="element-476" data-mp="32udmll78c0y9">
          <h2 className="text-3xl md:text-4xl font-bold text-brand-dark mb-4" data-id="element-477" data-mp="32vf0ryjjl3k7">
            Why Join the{' '}
            <span className="text-brand-primary" data-id="element-478" data-mp="37urm2eurm2ko">Simba Fleet?</span>
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-479" data-mp="3cqglmi6sbjgz">
            More than just a job – it's an opportunity to earn, learn, and make
            a difference.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-480" data-mp="3ls6elsgbeqgv">
          {benefits.map((benefit, idx) => <motion.div key={idx} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: idx * 0.1
        }} data-id="element-481" data-mp="3vi9g6h1i5cqt" data-mpi={idx}>
              <Card className="h-full hover:shadow-lg transition-shadow" data-id="element-482" data-mp="1xup6g09odxcd">
                <CardContent className="p-6" data-id="element-483" data-mp="1xskqul2ez9w9">
                  <div className="w-12 h-12 bg-brand-primary/10 rounded-xl flex items-center justify-center mb-4" data-id="element-484" data-mp="1tskaguvr8omk">
                    <benefit.icon className="w-6 h-6 text-brand-primary" data-id="element-485" data-mp="1ov1zz023a1cg" />
                  </div>
                  <h3 className="text-lg font-bold text-brand-dark mb-2" data-id="element-486" data-mp="1jvxscdutcbo8">
                    {benefit.title}
                  </h3>
                  <p className="text-slate-600" data-id="element-487" data-mp="2hhv9a6tjo8y2">{benefit.desc}</p>
                </CardContent>
              </Card>
            </motion.div>)}
        </div>
      </Section>

      {/* Requirements Section */}
      <Section className="bg-slate-100" data-id="element-488" data-mp="27qifh9o6r8pe">
        <div className="max-w-4xl mx-auto" data-id="element-489" data-mp="22ujulw22g8tq">
          <div className="text-center mb-12" data-id="element-490" data-mp="22rnymd9s3u7x">
            <h2 className="text-3xl md:text-4xl font-bold text-brand-dark mb-4" data-id="element-491" data-mp="309fma0h728qd">
              What We're Looking For
            </h2>
            <p className="text-lg text-slate-600" data-id="element-492" data-mp="2vguxjdd5altp">
              Think you'd be a great fit? Here's what we need:
            </p>
          </div>

          <Card data-id="element-493" data-mp="xm8w71e8swyw">
            <CardContent className="p-8" data-id="element-494" data-mp="xmqq3rdfmz3n">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-495" data-mp="xlpcpg8y20ir">
                {requirements.map((req, idx) => <motion.div key={idx} initial={{
                opacity: 0,
                x: -20
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: idx * 0.1
              }} className="flex items-start gap-3" data-id="element-496" data-mp="ou6zne7ao0tw" data-mpi={idx}>
                    <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" data-id="element-497" data-mp="1vashkl4kitmq" />
                    <span className="text-slate-700" data-id="element-498" data-mp="208138voxdfsq">{req}</span>
                  </motion.div>)}
              </div>

              <div className="mt-8 pt-8 border-t border-slate-200 text-center" data-id="element-499" data-mp="2aawknocyytte">
                <Button size="lg" onClick={() => setShowApplication(true)} data-id="element-500" data-mp="25g0t0pbna8gt">
                  I Meet These Requirements – Apply Now
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </Section>

      {/* Testimonials Section */}
      <Section data-id="element-501" data-mp="2t2gytlccuzna">
        <div className="text-center mb-12" data-id="element-502" data-mp="2t1xi27yfyhau">
          <h2 className="text-3xl md:text-4xl font-bold text-brand-dark mb-4" data-id="element-503" data-mp="2o4xk6kbqwkut">
            Hear From Our{' '}
            <span className="text-brand-primary" data-id="element-504" data-mp="324qa4dfg6x1q">Ambassadors</span>
          </h2>
          <p className="text-lg text-slate-600" data-id="element-505" data-mp="2y34q884hxii1">
            Real stories from students earning and making an impact.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-506" data-mp="17r78osiaqub5">
          {testimonials.map((testimonial, idx) => <motion.div key={idx} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: idx * 0.1
        }} data-id="element-507" data-mp="13qnbjoxq3qp0" data-mpi={idx}>
              <Card className="h-full" data-id="element-508" data-mp="1j3oand3m54cn">
                <CardContent className="p-6" data-id="element-509" data-mp="mg1hpp5r043y">
                  <div className="flex items-center gap-4 mb-4" data-id="element-510" data-mp="mhcnfuir7lxp">
                    <img src={testimonial.image} alt={testimonial.name} className="w-16 h-16 rounded-full object-cover ring-4 ring-slate-100" data-id="element-511" data-mp="qgblcv62iifn" />
                    <div data-id="element-512" data-mp="1087myg7d4qom">
                      <h4 className="font-bold text-brand-dark" data-id="element-513" data-mp="108r2xrdgd500">
                        {testimonial.name}
                      </h4>
                      <p className="text-sm text-slate-500" data-id="element-514" data-mp="7ptwj1ocw6zk">
                        {testimonial.role}
                      </p>
                      <p className="text-xs text-slate-400" data-id="element-515" data-mp="6kphf3bokdo">
                        {testimonial.university}
                      </p>
                    </div>
                  </div>

                  <p className="text-slate-600 mb-4 italic" data-id="element-516" data-mp="3mczqto6o4fv9">
                    "{testimonial.quote}"
                  </p>

                  <div className="flex gap-4 pt-4 border-t border-slate-100" data-id="element-517" data-mp="3icfqycupmy5f">
                    <div data-id="element-518" data-mp="3dee8nu53z2v0">
                      <div className="text-lg font-bold text-emerald-600" data-id="element-519" data-mp="3dfpfy3xrisqv">
                        {testimonial.earnings}
                      </div>
                      <div className="text-xs text-slate-500" data-id="element-520" data-mp="33n1m9i22x4wd">Monthly</div>
                    </div>
                    <div className="border-l border-slate-200 pl-4" data-id="element-521" data-mp="2yphx3n3uf7pn">
                      <div className="text-lg font-bold text-brand-primary" data-id="element-522" data-mp="2un4xzhjlq81u">
                        {testimonial.verifications}
                      </div>
                      <div className="text-xs text-slate-500" data-id="element-523" data-mp="2pojzpnnwtyg1">
                        Verifications
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </div>
      </Section>

      {/* CTA Section */}
      <Section className="bg-gradient-to-br from-brand-dark via-brand-dark-secondary to-brand-dark text-white" data-id="element-524" data-mp="264cstzvahe3r">
        <div className="max-w-3xl mx-auto text-center" data-id="element-525" data-mp="2b3637jfa4ozp">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-526" data-mp="2ys8jony6gbsm">
            <Award className="w-16 h-16 text-brand-primary mx-auto mb-6" data-id="element-527" data-mp="2tzfgi4icxbin" />
            <h2 className="text-4xl font-bold mb-4" data-id="element-528" data-mp="3hpry673emmmf">
              Ready to Join the Fleet?
            </h2>
            <p className="text-xl text-slate-300 mb-8" data-id="element-529" data-mp="3mg8cruiyoead">
              Start earning, gain valuable experience, and help make student
              housing safer.
            </p>
            <Button size="lg" className="bg-brand-primary hover:bg-brand-light text-white" onClick={() => setShowApplication(true)} data-id="element-530" data-mp="1az6gdk35j61o">
              Apply to Become an Ambassador
              <ArrowRight className="ml-2 w-5 h-5" data-id="element-531" data-mp="1frzkc5qsqacp" />
            </Button>
            <p className="text-sm text-slate-400 mt-4" data-id="element-532" data-mp="s2nffi2i4og7">
              Applications reviewed within 48 hours • No experience required
            </p>
          </motion.div>
        </div>
      </Section>
    </div>;
}